import * as Api from '@ViewModels';
import * as React from 'react';
import { ITemplatesIndustryWithCategories } from '../../../../../models/AdminModels';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { usePostcardTextEditor } from '../../../../../web/containers/PostcardComposer/hooks/usePostcardTextEditor';

interface IState {
	displayName: string;
	featured: boolean;
	keywords: string;
	postcardContent: string;
	providerProductIds: Record<Api.HandwrittenCardProvider, string>;
	selectedIndustryOptions: Api.ITemplateStatus[];
	summary: string;
	templateImageFile?: File;
	templateImageUrl?: string;
	firstSelection?: boolean;
}

type Action =
	| { type: 'SET_FEATURED'; value: boolean }
	| { type: 'SET_DISPLAY_NAME'; value: string }
	| { type: 'SET_KEYWORDS'; value: string }
	| { type: 'SET_PRODUCT_ID'; value: { provider: Api.HandwrittenCardProvider; id: string } }
	| { type: 'SET_POSTCARD_CONTENT'; value: string }
	| { type: 'SET_SUMMARY'; value: string }
	| {
			type: 'ADD_TO_INDUSTRY_OPTIONS';
			value: { categoryName: string; industry: Api.Industry; catOptions: ITemplatesIndustryWithCategories[] };
	  }
	| { type: 'REMOVE_FROM_INDUSTRY_OPTIONS'; value: { industry: Api.Industry } }
	| { type: 'SET_TEMPLATE_IMAGE_FILE'; value: File }
	| { type: 'INIT'; value: IState };

const reducer = (state: IState, action: Action) => {
	let newState = { ...state };
	switch (action.type) {
		case 'SET_FEATURED': {
			newState.featured = action.value;
			break;
		}
		case 'SET_DISPLAY_NAME': {
			newState.displayName = action.value;
			break;
		}
		case 'SET_KEYWORDS': {
			newState.keywords = action.value;
			break;
		}
		case 'SET_PRODUCT_ID': {
			newState.providerProductIds[action.value.provider] = action.value.id;
			break;
		}
		case 'SET_POSTCARD_CONTENT': {
			newState.postcardContent = action.value;
			break;
		}
		case 'SET_SUMMARY': {
			newState.summary = action.value;
			break;
		}
		case 'ADD_TO_INDUSTRY_OPTIONS': {
			/**
			 * @TODO take a look at where there is an issue with multiple selections
			 */
			// if (!state.firstSelection) {
			// 	const newList: Api.ITemplateStatus[] = getCategoriesByName(action.value.catOptions, action.value.categoryName);
			// 	newState.selectedIndustryOptions = [...state.selectedIndustryOptions, ...newList];
			// 	newState.firstSelection = true;
			// 	break;
			// }
			const newOptions = state.selectedIndustryOptions.filter(option => option.industry !== action.value.industry);
			newOptions.push({ category: action.value.categoryName, industry: action.value.industry });
			newState.selectedIndustryOptions = newOptions;
			break;
		}
		case 'REMOVE_FROM_INDUSTRY_OPTIONS': {
			newState.selectedIndustryOptions = state.selectedIndustryOptions.filter(
				option => option.industry !== action.value.industry
			);
			break;
		}
		case 'SET_TEMPLATE_IMAGE_FILE': {
			newState.templateImageFile = action.value;
			break;
		}
		case 'INIT': {
			newState = action.value;
			break;
		}
		default:
			throw new Error('Unknown action type');
	}
	return newState;
};

export function useHwcTemplateForm() {
	const errorMessages = useErrorMessages();
	const [
		{
			displayName,
			featured,
			keywords,
			postcardContent,
			providerProductIds,
			selectedIndustryOptions,
			summary,
			templateImageFile,
			templateImageUrl,
			firstSelection,
		},
		dispatch,
	] = React.useReducer(reducer, {
		displayName: '',
		featured: false,
		keywords: '',
		postcardContent: '',
		providerProductIds: {} as Record<Api.HandwrittenCardProvider, string>,
		selectedIndustryOptions: [],
		summary: '',
		templateImageFile: null,
		templateImageUrl: '',
		firstSelection: false,
	});
	const { postcardText, getPostcardTextEditorProps } = usePostcardTextEditor({
		content: postcardContent,
		handwritingStyleEnabled: false,

		onContentChanged: content => dispatch({ type: 'SET_POSTCARD_CONTENT', value: content }),
	});
	const templateImagePreviewURL = React.useMemo(() => {
		if (templateImageFile) {
			return URL.createObjectURL(templateImageFile);
		}
		return templateImageUrl ? templateImageUrl : '';
	}, [templateImageFile, templateImageUrl]);
	const validate = () => {
		const errMessages = [];
		if (!displayName) {
			errMessages.push('Template display name is empty');
		}
		if (selectedIndustryOptions.length === 0) {
			errMessages.push(`At least one industry-category pair must be selected`);
		}
		if (!templateImageFile && !templateImageUrl) {
			errMessages.push(`Template image is required`);
		}
		if (
			!providerProductIds ||
			Object.keys(providerProductIds).filter(x => !!providerProductIds[x as Api.HandwrittenCardProvider]).length === 0
		) {
			errMessages.push(`Product Id is required`);
		}
		if (errMessages.length > 0) {
			errorMessages.push({
				messages: errMessages,
			});
			return;
		}
		return errMessages.length === 0;
	};
	return {
		dispatch,
		displayName,
		featured,
		getPostcardTextEditorProps,
		keywords,
		postcardContent,
		postcardText,
		providerProductIds,
		selectedIndustryOptions,
		summary,
		templateImageFile,
		templateImagePreviewURL,
		validate,
		firstSelection,
	};
}
